import { type CSSProperties, type ReactElement } from 'react';

import { type Fill, experienceBarOf, fillOf, levelOf, pointsLabel, readoutOf } from './hud-model';

/**
 * The player's own numbers, laid out the way D2 lays them out: life orb bottom
 * left, mana orb bottom right, the experience bar spanning the gap between them.
 *
 * ## It draws numbers. It does not produce them.
 *
 * Every value in `HudVitals` and `HudProgress` arrives already decided. This
 * panel does not know the experience curve, when a level is earned, what a
 * skill costs or how fast mana comes back — those are rules, rules live in
 * `packages/core`, and the client and the server run the same copy of them
 * (`ARCHITECTURE.md`). What is left here is arithmetic about *pixels*: a
 * percentage for a fill, a string for a readout. `./hud-model.ts` owns even
 * that much, so the two things that can actually be wrong — a divide by zero
 * and a fill that paints outside its own orb — are provable without a browser.
 *
 * `experienceIntoLevel` and `experienceForNextLevel` are the shape of that
 * split: the caller has already subtracted the level's floor, so this panel
 * never has to know what the floor was.
 *
 * ## It must not take a click
 *
 * The canvas underneath is the game, and the game is played by clicking the
 * floor. So the whole panel is `pointer-events: none` and there is nothing
 * interactive inside it — no button, nothing focusable. A badge saying you have
 * points to spend is *an affordance to open a tree*, not the tree; whatever
 * opens the tree will be a key or a control of its own, and it will have to opt
 * back into pointer events deliberately. The suite asserts that no element in
 * here has done so, because the failure mode is invisible until a player clicks
 * near the bottom of the screen and their character does not move.
 *
 * ## It positions itself over its parent, and its parent is not this file's
 *
 * The root is `position: absolute; inset: 0`, so whoever mounts it puts it in a
 * positioned box the size of the canvas. That is deliberately somebody else's
 * decision — see the note on the layout in the handover — because `App.tsx`
 * currently centres a fixed 960x540 canvas in a page-level flex column, and a
 * HUD glued to the viewport would not line up with it.
 *
 * ## Placeholder art
 *
 * Primitives — two circles, a trough and a fill — in the house palette
 * (`index.css`). Styles are inline rather than in a stylesheet because this file
 * is additive and `index.css` belongs to the shell; a class-based pass belongs
 * with the real art.
 */

export interface HudVitals {
  readonly life: number;
  readonly maximumLife: number;
  readonly mana: number;
  readonly maximumMana: number;
}

export interface HudProgress {
  readonly level: number;
  /** Experience earned inside the current level, already resolved by the caller. */
  readonly experienceIntoLevel: number;
  /** Experience the current level spans. 0 at max level. */
  readonly experienceForNextLevel: number;
  readonly unspentSkillPoints: number;
  readonly unspentStatPoints: number;
}

export interface HudProps {
  readonly vitals: HudVitals;
  readonly progress: HudProgress;
}

/** Ink that stays readable over a dark cave, and over a lit one. */
const OUTLINE = '0 1px 2px #000, 0 0 3px #000';

const styles = {
  root: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: '0.75rem',
    padding: '0.75rem',
    // The whole point. See the header.
    pointerEvents: 'none',
    userSelect: 'none',
    color: '#e8dcc0',
    fontSize: '0.75rem',
    lineHeight: 1.2,
  },
  orb: {
    position: 'relative',
    flex: '0 0 auto',
    width: '5rem',
    height: '5rem',
    borderRadius: '50%',
    overflow: 'hidden',
    border: '2px solid #55503f',
    background: '#0b0b0d',
    boxShadow: 'inset 0 0 12px #000',
  },
  orbFill: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
  },
  orbReadout: {
    position: 'relative',
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    fontVariantNumeric: 'tabular-nums',
    textShadow: OUTLINE,
  },
  middle: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '0.3rem',
    minWidth: 0,
    paddingBottom: '0.4rem',
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap',
    textShadow: OUTLINE,
  },
  badge: {
    padding: '0.05rem 0.45rem',
    border: '1px solid #8a7a4a',
    borderRadius: '999px',
    background: '#262218',
    color: '#f0e2b8',
    textShadow: 'none',
    whiteSpace: 'nowrap',
  },
  trough: {
    position: 'relative',
    height: '0.5rem',
    border: '1px solid #33312c',
    borderRadius: '3px',
    background: '#0e0d10',
    overflow: 'hidden',
    boxShadow: 'inset 0 0 6px #000',
  },
  barFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    background: '#cbb27a',
  },
} satisfies Record<string, CSSProperties>;

interface OrbProps {
  readonly id: 'life' | 'mana';
  readonly label: 'Life' | 'Mana';
  readonly colour: string;
  readonly fill: Fill;
}

/**
 * One pool, drawn as a globe that empties downwards.
 *
 * The percentage lands on the element twice — once as the height a browser
 * paints and once as `data-fill`, which is a number a test or the browser suite
 * can read without parsing a style attribute. They come from the same `Fill`, so
 * they cannot disagree.
 */
const Orb = ({ id, label, colour, fill }: OrbProps): ReactElement => {
  const readout = readoutOf(fill);

  return (
    <div
      data-testid={`hud-${id}`}
      style={styles.orb}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={fill.maximum}
      aria-valuenow={fill.current}
      aria-valuetext={`${Math.round(fill.current)} of ${Math.round(fill.maximum)}`}
    >
      <div
        data-testid={`hud-${id}-fill`}
        data-fill={fill.percent}
        style={{ ...styles.orbFill, height: `${fill.percent}%`, background: colour }}
      />
      <span data-testid={`hud-${id}-readout`} style={styles.orbReadout}>
        {readout}
      </span>
    </div>
  );
};

export const Hud = ({ vitals, progress }: HudProps): ReactElement => {
  const life = fillOf(vitals.life, vitals.maximumLife);
  const mana = fillOf(vitals.mana, vitals.maximumMana);
  const experience = experienceBarOf(progress);

  const atMaximum = experience.kind === 'max-level';
  // A maximum-level bar is drawn full against a span of one, because there is
  // no span to take a proportion of. See `experienceBarOf`.
  const percent = atMaximum ? 100 : experience.fill.percent;
  const level = levelOf(progress.level);

  return (
    <div data-testid="hud" style={styles.root} aria-label="Character status">
      <Orb id="life" label="Life" colour="#8e1f18" fill={life} />

      <div style={styles.middle}>
        <div style={styles.meta}>
          <span data-testid="hud-level">{`Level ${level}`}</span>
          {progress.unspentSkillPoints > 0 && (
            <span data-testid="hud-skill-points" style={styles.badge}>
              {pointsLabel(progress.unspentSkillPoints, 'skill')}
            </span>
          )}
          {progress.unspentStatPoints > 0 && (
            <span data-testid="hud-stat-points" style={styles.badge}>
              {pointsLabel(progress.unspentStatPoints, 'stat')}
            </span>
          )}
        </div>

        <div
          data-testid="hud-experience"
          style={styles.trough}
          role="progressbar"
          aria-label="Experience"
          aria-valuemin={0}
          aria-valuemax={atMaximum ? 1 : experience.fill.maximum}
          aria-valuenow={atMaximum ? 1 : experience.fill.current}
          aria-valuetext={
            atMaximum
              ? 'Maximum level'
              : `${Math.round(experience.fill.current)} of ${Math.round(experience.fill.maximum)}`
          }
        >
          <div
            data-testid="hud-experience-fill"
            data-fill={percent}
            style={{ ...styles.barFill, width: `${percent}%` }}
          />
        </div>
      </div>

      <Orb id="mana" label="Mana" colour="#22459b" fill={mana} />
    </div>
  );
};
